import { useEffect, useState } from 'preact/hooks';
import { Check, Crown, Flame, Grid3x3, Lock, Paintbrush, PartyPopper, PawPrint, Pencil, Plus, Shirt, Sparkles, Trash, Circle } from 'lucide-preact';
import { ARMOR_STYLES, BANNER_BY_ID, BANNERS, DEATH_EFFECTS, DEATH_FX_BY_ID, PET_BY_ID, PET_STYLES, SKIN_PRESETS } from '@vm/content';
import type { CustomSkin, ItemType } from '@vm/shared';
import { CharacterPreview } from '../three/CharacterPreview';
import type { SkinEditorProps } from '../skin-editor/SkinEditor';
import { buyItem, deleteCustomSkin, equipCosmetic, equipPetStyle, saveCustomSkin } from '../state/actions';
import { equippedPet, itemInfo, shopState, SOURCE_LABEL, stylePreviewPet, type CosmeticState, type ItemInfo } from '../state/content';
import { go, replace, route } from '../state/nav';
import { catalog, cosmetics, isOffline, loadout, me, wallet } from '../state/store';
import { fmtNum, hex } from '../ui/format';
import { useCompact, useNarrow } from '../ui/hooks';
import { Icon } from '../ui/icons';
import { AsyncButton, confirmDialog, Price, RarityTag, Screen, SkinImg, Spinner, Tabs } from '../ui/kit';
import { PetPreview } from '../ui/PetPreview';

type Tab = 'skins' | 'custom' | 'armor' | 'effects' | 'banners' | 'pets';
/** Matches the API's skin slot count (slots 0-5). */
const CUSTOM_SLOTS = 6;

// The skin editor is only needed on demand, so it lives in its own chunk.
type SkinEditorModule = typeof import('../skin-editor/SkinEditor');
let editorModule: Promise<SkinEditorModule> | null = null;
const loadEditor = (): Promise<SkinEditorModule> => (editorModule ??= import('../skin-editor/SkinEditor'));

function LazySkinEditor(props: SkinEditorProps) {
  const [mod, setMod] = useState<SkinEditorModule | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    loadEditor()
      .then(setMod)
      .catch(() => setFailed(true));
  }, []);
  if (mod) return <mod.SkinEditor {...props} />;
  return (
    <div class="wd-editor-loading">
      {failed ? (
        <>
          <p>Couldn't open the skin editor.</p>
          <button class="btn" onClick={props.onCancel}>
            Back
          </button>
        </>
      ) : (
        <Spinner label="Opening the paint shop…" />
      )}
    </div>
  );
}

interface Selection {
  /** A 'petstyle' with id '' is the pet's own colors (always owned). */
  type: ItemType;
  id: string;
  /** Skin/armor to preview when this item is selected. */
  skin?: string;
  armor?: string;
}

function StateChip(props: { state: CosmeticState; type: ItemType; id: string }) {
  const info = itemInfo(props.type, props.id);
  switch (props.state) {
    case 'equipped':
      return (
        <span class="arm-chip equipped">
          <Check size={14} strokeWidth={3.5} /> Equipped
        </span>
      );
    case 'owned':
      return <span class="arm-chip owned">Owned</span>;
    case 'locked':
      return (
        <span class="arm-chip locked">
          <Lock size={13} strokeWidth={3} /> Lv {info.unlockLevel}
        </span>
      );
    case 'unavailable':
      return <span class="arm-chip source">{SOURCE_LABEL[info.source ?? ''] ?? 'Special'}</span>;
    default:
      return <Price price={info.price} currency={info.currency} class="arm-chip-price" />;
  }
}

const FX_ICON: Record<string, preact.ComponentChildren> = {
  shatter: <Grid3x3 size={34} />,
  confetti: <PartyPopper size={34} />,
  pixel: <Grid3x3 size={34} />,
  fireworks: <Sparkles size={34} />,
  bubbles: <Circle size={34} />,
  gold: <Crown size={34} />,
};

/** Extra ids of a type that only exist in the live catalog. */
function catalogIds(type: ItemType, known: string[]): string[] {
  return (catalog.value?.items ?? []).filter((e) => e.type === type && !known.includes(e.id)).map((e) => e.id);
}

export function BannerView(props: { id: string; class?: string }) {
  const b = BANNER_BY_ID[props.id];
  const [c1, c2] = b?.colors ?? [0x3a4a6a, 0xffd24a];
  return (
    <span class={`banner-view ${props.class ?? ''}`} style={{ background: `linear-gradient(135deg, ${hex(c1)}, ${hex(c1)} 55%, ${hex(c2)})` }}>
      <span class="banner-view-icon" style={{ color: hex(c2) }}>
        <Icon name={b?.icon ?? 'shield'} size={26} strokeWidth={2.6} />
      </span>
    </span>
  );
}

function ItemCard(props: { sel: Selection; selected: boolean; onClick: () => void; children: preact.ComponentChildren; state: CosmeticState; name: string; rarity: string }) {
  return (
    <button class={`arm-card wd-card r-${props.rarity} ${props.selected ? 'is-on' : ''} st-${props.state}`} onClick={props.onClick}>
      <span class="arm-card-img">{props.children}</span>
      <span class="arm-card-name">{props.name}</span>
      <StateChip state={props.state} type={props.sel.type} id={props.sel.id} />
    </button>
  );
}

const ORIGINAL_COLORS: Omit<ItemInfo, 'type' | 'id'> = { name: 'Original Colors', rarity: 'common', price: 0, currency: 'coins', unlockLevel: 1, source: 'starter' };

function ActionBar(props: { sel: Selection | null; onEquip: (sel: Selection) => Promise<unknown> }) {
  const sel = props.sel;
  if (!sel) return null;
  const c = cosmetics.value;
  const equipped =
    (sel.type === 'skin' && c.skin === `preset:${sel.id}`) ||
    (sel.type === 'armor' && c.armor === sel.id) ||
    (sel.type === 'deathfx' && (c.deathFx ?? 'shatter') === sel.id) ||
    (sel.type === 'banner' && (c.banner ?? 'recruit') === sel.id) ||
    (sel.type === 'petstyle' && (c.petStyle ?? '') === sel.id);
  const original = sel.type === 'petstyle' && !sel.id;
  const state: CosmeticState = original ? (equipped ? 'equipped' : 'owned') : shopState(sel.type, sel.id, equipped);
  const info = original ? { ...ORIGINAL_COLORS, type: sel.type, id: sel.id } : itemInfo(sel.type, sel.id);
  const w = wallet.value;
  const have = info.currency === 'gems' ? w.gems : w.coins;
  return (
    <div class="wd-action">
      <div class="wd-action-name">
        <b>{info.name}</b>
        <RarityTag rarity={info.rarity} />
      </div>
      {state === 'equipped' && (
        <button class="btn big" disabled>
          <Check size={20} strokeWidth={3} /> Equipped
        </button>
      )}
      {state === 'owned' && (
        <AsyncButton class="green big" onClick={() => props.onEquip(sel)}>
          Equip
        </AsyncButton>
      )}
      {state === 'locked' && (
        <button class="btn big" disabled>
          <Lock size={20} /> Unlocks at level {info.unlockLevel}
        </button>
      )}
      {state === 'unavailable' && (
        <button class="btn big" disabled>
          Earned from {SOURCE_LABEL[info.source ?? ''] ?? 'special events'}
        </button>
      )}
      {state === 'buy' && (
        <AsyncButton
          class={`${info.currency === 'gems' ? 'gem' : 'primary'} big`}
          disabled={have < info.price || isOffline.value}
          onClick={async () => {
            const ok = await confirmDialog({
              title: `Buy ${info.name}?`,
              body: (
                <span class="confirm-price">
                  <Price price={info.price} currency={info.currency} />
                </span>
              ),
              confirm: 'Buy',
            });
            if (ok && (await buyItem(sel.type, sel.id, info.name))) await props.onEquip(sel);
          }}
        >
          {isOffline.value ? 'Offline' : have < info.price ? `Need ${fmtNum(info.price - have)} more` : 'Buy'} <Price price={info.price} currency={info.currency} />
        </AsyncButton>
      )}
    </div>
  );
}

function CustomSlots(props: { onEdit: (slot: number, skin?: CustomSkin) => void; onPreview: (skin: string) => void }) {
  const skins = me.value?.customSkins ?? [];
  const c = cosmetics.value;
  const slots = Array.from({ length: Math.max(CUSTOM_SLOTS, skins.length) }, (_, i) => skins.find((s) => s.slot === i) ?? null);
  return (
    <div class="custom-grid">
      {slots.map((s, i) =>
        s ? (
          <div key={i} class={`custom-slot ${c.skin === s.data ? 'is-equipped' : ''}`}>
            <button class="custom-slot-img" onClick={() => props.onPreview(s.data)}>
              <SkinImg skin={s.data} armor={c.armor} scale={5} bare />
            </button>
            <b class="custom-slot-name">{s.name}</b>
            <div class="custom-slot-actions">
              {c.skin === s.data ? (
                <span class="arm-chip equipped">
                  <Check size={14} strokeWidth={3.5} /> Worn
                </span>
              ) : (
                <AsyncButton class="sm green" onClick={() => equipCosmetic({ skin: s.data })}>
                  Wear
                </AsyncButton>
              )}
              <button class="btn sm icon" aria-label={`Edit ${s.name}`} disabled={isOffline.value} onClick={() => props.onEdit(i, s)}>
                <Pencil size={16} />
              </button>
              <AsyncButton
                class="sm icon"
                aria-label={`Delete ${s.name}`}
                disabled={isOffline.value}
                onClick={async () => {
                  if (await confirmDialog({ title: `Delete "${s.name}"?`, body: "This can't be undone.", confirm: 'Delete', danger: true })) await deleteCustomSkin(i);
                }}
              >
                <Trash size={16} />
              </AsyncButton>
            </div>
          </div>
        ) : (
          <button key={i} class="custom-slot is-empty" disabled={isOffline.value} onClick={() => props.onEdit(i)}>
            <span class="custom-plus">
              <Plus size={34} strokeWidth={3} />
            </span>
            <b>Create skin</b>
            <small>Slot {i + 1}</small>
          </button>
        ),
      )}
    </div>
  );
}

export function WardrobeScreen() {
  const tab = (route.value.params.tab as Tab) || 'skins';
  const c = cosmetics.value;
  const lv = me.value?.progression.level ?? 1;
  const [sel, setSel] = useState<Selection | null>(null);
  const [editing, setEditing] = useState<{ slot: number; skin?: CustomSkin } | null>(null);
  const [customPreview, setCustomPreview] = useState<string | null>(null);
  const previewSkin = sel?.skin ?? customPreview ?? c.skin;
  const previewArmor = sel?.armor ?? c.armor;
  const primary = loadout.value.weapons.find((w) => !!w);
  const compact = useCompact();
  const narrow = useNarrow();
  const petId = stylePreviewPet();
  const petStyle = sel?.type === 'petstyle' ? sel.id : c.petStyle ?? '';

  const equip = async (s: Selection): Promise<unknown> => {
    if (s.type === 'skin') return equipCosmetic({ skin: `preset:${s.id}` });
    if (s.type === 'armor') return equipCosmetic({ armor: s.id });
    if (s.type === 'deathfx') return equipCosmetic({ deathFx: s.id });
    if (s.type === 'banner') return equipCosmetic({ banner: s.id });
    if (s.type === 'petstyle') return equipPetStyle(s.id);
    return undefined;
  };
  const choose = (s: Selection): void => {
    setCustomPreview(null);
    setSel(s);
  };

  const presets = SKIN_PRESETS.filter((p) => p.unlockLevel < 900);
  const fxIds = [...DEATH_EFFECTS.map((d) => d.id), ...catalogIds('deathfx', DEATH_EFFECTS.map((d) => d.id))];
  const bannerIds = [...BANNERS.map((b) => b.id), ...catalogIds('banner', BANNERS.map((b) => b.id))];

  return (
    <Screen
      title="Wardrobe"
      icon={<Shirt size={26} />}
      class="wardrobe"
      tabs={
        <Tabs<Tab>
          value={tab}
          onChange={(t) => {
            setSel(null);
            setCustomPreview(null);
            replace('wardrobe', { tab: t });
          }}
          tabs={[
            { id: 'skins', label: 'Skins' },
            { id: 'custom', label: 'Custom', icon: <Paintbrush size={16} /> },
            { id: 'armor', label: 'Armor' },
            { id: 'effects', label: 'Effects' },
            { id: 'banners', label: 'Banners' },
            { id: 'pets', label: 'Pet Styles', icon: <PawPrint size={16} /> },
          ]}
        />
      }
    >
      <div class="wd">
        <div class="wd-preview">
          <div class="wd-stage" aria-hidden="true" />
          {tab === 'pets' ? (
            <div class="wd-pet">
              <PetPreview petId={petId} styleId={petStyle} size={compact ? 150 : narrow ? 170 : 240} />
              <span class="wd-pet-name">{PET_BY_ID[petId]?.name}</span>
            </div>
          ) : (
            <CharacterPreview skin={previewSkin} armor={previewArmor} weapon={primary} rotate className="wd-char" />
          )}
          <ActionBar sel={sel} onEquip={equip} />
        </div>
        <div class="wd-main scr-scroll" key={tab}>
          {tab === 'skins' && (
            <div class="arm-grid">
              {presets.map((p) => {
                const s: Selection = { type: 'skin', id: p.id, skin: `preset:${p.id}` };
                const state = shopState('skin', p.id, c.skin === `preset:${p.id}`);
                return (
                  <ItemCard key={p.id} sel={s} name={p.name} rarity={p.rarity} state={state} selected={sel?.id === p.id && sel.type === 'skin'} onClick={() => choose(s)}>
                    <SkinImg skin={`preset:${p.id}`} armor={c.armor} scale={4} bare />
                  </ItemCard>
                );
              })}
            </div>
          )}
          {tab === 'custom' && (
            <>
              <p class="wd-hint">Paint your own marine voxel by voxel. Keep it friendly — skins that break the rules can be removed.</p>
              <CustomSlots
                onEdit={(slot, skin) => {
                  void loadEditor().catch(() => {});
                  setEditing({ slot, skin });
                }}
                onPreview={(skin) => {
                  setSel(null);
                  setCustomPreview(skin);
                }}
              />
            </>
          )}
          {tab === 'armor' && (
            <div class="arm-grid">
              {ARMOR_STYLES.map((a) => {
                const s: Selection = { type: 'armor', id: a.id, armor: a.id };
                const state = shopState('armor', a.id, c.armor === a.id);
                return (
                  <ItemCard key={a.id} sel={s} name={a.name} rarity={a.rarity} state={state} selected={sel?.id === a.id && sel.type === 'armor'} onClick={() => choose(s)}>
                    <SkinImg skin={c.skin} armor={a.id} scale={4} />
                  </ItemCard>
                );
              })}
            </div>
          )}
          {tab === 'effects' && (
            <div class="arm-grid">
              {fxIds.map((id) => {
                const info = itemInfo('deathfx', id);
                const s: Selection = { type: 'deathfx', id };
                const state = shopState('deathfx', id, (c.deathFx ?? 'shatter') === id);
                const style = DEATH_FX_BY_ID[id]?.style ?? 'shatter';
                return (
                  <ItemCard key={id} sel={s} name={info.name} rarity={info.rarity} state={state} selected={sel?.id === id && sel.type === 'deathfx'} onClick={() => choose(s)}>
                    <span class={`fx-icon fx-${style}`}>{FX_ICON[style] ?? <Flame size={34} />}</span>
                  </ItemCard>
                );
              })}
            </div>
          )}
          {tab === 'banners' && (
            <div class="arm-grid">
              {bannerIds.map((id) => {
                const info = itemInfo('banner', id);
                const s: Selection = { type: 'banner', id };
                const state = shopState('banner', id, (c.banner ?? 'recruit') === id);
                return (
                  <ItemCard key={id} sel={s} name={info.name} rarity={info.rarity} state={state} selected={sel?.id === id && sel.type === 'banner'} onClick={() => choose(s)}>
                    <BannerView id={id} />
                  </ItemCard>
                );
              })}
            </div>
          )}
          {tab === 'pets' && (
            <>
              <p class="wd-hint">
                Pet styles recolor whichever pet you bring into battle.
                {!equippedPet() && ' You have no pet with you right now: pick one in the Armory.'}{' '}
                <button class="btn sm wd-hint-btn" onClick={() => go('armory', { tab: 'pets' })}>
                  <PawPrint size={15} /> Pets
                </button>
              </p>
              <div class="arm-grid">
                {['', ...PET_STYLES.map((ps) => ps.id)].map((id) => {
                  const s: Selection = { type: 'petstyle', id };
                  const equipped = (c.petStyle ?? '') === id;
                  const state: CosmeticState = id ? shopState('petstyle', id, equipped) : equipped ? 'equipped' : 'owned';
                  const info = id ? itemInfo('petstyle', id) : ORIGINAL_COLORS;
                  return (
                    <ItemCard key={id || 'original'} sel={s} name={info.name} rarity={info.rarity} state={state} selected={sel?.type === 'petstyle' && sel.id === id} onClick={() => choose(s)}>
                      <PetPreview petId={petId} styleId={id} size={compact ? 60 : 88} />
                    </ItemCard>
                  );
                })}
              </div>
            </>
          )}
          {lv < 2 && tab === 'skins' && <p class="wd-hint">More looks unlock as you level up. Gems buy premium looks — never power.</p>}
        </div>
      </div>
      {editing && (
        <div class="wd-editor-layer">
          <LazySkinEditor
            initial={editing.skin?.data ?? c.skin}
            armor={c.armor}
            initialName={editing.skin?.name ?? `My Skin ${editing.slot + 1}`}
            onCancel={() => setEditing(null)}
            onSave={async (data, name) => {
              const ok = await saveCustomSkin(editing.slot, name.trim().slice(0, 24) || `My Skin ${editing.slot + 1}`, data);
              if (ok) setEditing(null);
            }}
          />
        </div>
      )}
    </Screen>
  );
}
